import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, RotateCw, RotateCcw } from "lucide-react";

interface FileViewerProps {
  file: File | null;
  open: boolean;
  onClose: () => void;
}

import { pdfjsLib, getPdfDocument } from "@/lib/pdfjs";

/**
 * Caché de páginas ya rasterizadas por archivo. Guardamos los <canvas> tal cual
 * (sin codificar a PNG/base64), lo que evita el coste de toDataURL + decodificar
 * la imagen de nuevo: es mucho más rápido y sin pérdida de calidad.
 */
const pageCache = new WeakMap<File, HTMLCanvasElement[]>();

/**
 * Muestra un <canvas> ya renderizado y, al rotarse 90°/270°, lo re-escala para
 * caber completamente en el contenedor en lugar de desbordar.
 */
const RotatableCanvas = ({
  canvas,
  rotation,
  label,
}: {
  canvas: HTMLCanvasElement;
  rotation: number;
  label: string;
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const holderRef = useRef<HTMLDivElement>(null);
  const [maxW, setMaxW] = useState(0);
  const [maxH, setMaxH] = useState<number>(() =>
    typeof window !== "undefined" ? window.innerHeight * 0.75 : 600,
  );

  useEffect(() => {
    const el = wrapRef.current?.parentElement;
    if (!el) return;
    const upd = () => {
      setMaxW(el.clientWidth);
      setMaxH(window.innerHeight * 0.75);
    };
    upd();
    const ro = new ResizeObserver(upd);
    ro.observe(el);
    window.addEventListener("resize", upd);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", upd);
    };
  }, []);

  // Insertamos el nodo canvas directamente (sin copias ni codificaciones).
  useEffect(() => {
    const holder = holderRef.current;
    if (!holder) return;
    canvas.setAttribute("aria-label", label);
    canvas.className = "rounded-lg border block";
    holder.appendChild(canvas);
    return () => {
      if (canvas.parentNode === holder) holder.removeChild(canvas);
    };
  }, [canvas, label]);

  const rotated = rotation % 180 !== 0;
  const natW = canvas.width;
  const natH = canvas.height;
  let dispW = 0;
  let dispH = 0;
  if (maxW) {
    const boxW = rotated ? natH : natW;
    const boxH = rotated ? natW : natH;
    const scale = Math.min(maxW / boxW, maxH / boxH, 1);
    dispW = boxW * scale;
    dispH = boxH * scale;
  }
  const cssW = rotated ? dispH : dispW;
  const cssH = rotated ? dispW : dispH;

  useEffect(() => {
    canvas.style.width = cssW ? `${cssW}px` : "";
    canvas.style.height = cssH ? `${cssH}px` : "";
  }, [canvas, cssW, cssH]);

  return (
    <div
      ref={wrapRef}
      className="relative flex items-center justify-center"
      style={{ width: dispW || "100%", height: dispH || undefined }}
    >
      <div
        ref={holderRef}
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
          transformOrigin: "center center",
          transition: "transform 0.2s ease",
        }}
      />
    </div>
  );
};

/**
 * Renderiza una imagen que, al rotarse 90°/270°, se re-escala para caber
 * completamente en el contenedor (ancho disponible + alto máximo del visor)
 * en lugar de desbordar y forzar scroll horizontal.
 */
const RotatableImage = ({
  src,
  alt,
  rotation,
}: {
  src: string;
  alt: string;
  rotation: number;
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [nat, setNat] = useState<{ w: number; h: number } | null>(null);
  const [maxW, setMaxW] = useState(0);
  const [maxH, setMaxH] = useState<number>(() =>
    typeof window !== "undefined" ? window.innerHeight * 0.75 : 600,
  );

  useEffect(() => {
    const el = wrapRef.current?.parentElement;
    if (!el) return;
    const upd = () => {
      setMaxW(el.clientWidth);
      setMaxH(window.innerHeight * 0.75);
    };
    upd();
    const ro = new ResizeObserver(upd);
    ro.observe(el);
    window.addEventListener("resize", upd);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", upd);
    };
  }, []);

  const rotated = rotation % 180 !== 0;
  let dispW = 0;
  let dispH = 0;
  if (nat && maxW) {
    const boxW = rotated ? nat.h : nat.w;
    const boxH = rotated ? nat.w : nat.h;
    const scale = Math.min(maxW / boxW, maxH / boxH, 1);
    dispW = boxW * scale;
    dispH = boxH * scale;
  }
  const imgW = rotated ? dispH : dispW;
  const imgH = rotated ? dispW : dispH;

  return (
    <div
      ref={wrapRef}
      className="relative flex items-center justify-center"
      style={{ width: dispW || "100%", height: dispH || undefined }}
    >
      <img
        src={src}
        alt={alt}
        onLoad={(e) => {
          const i = e.currentTarget;
          setNat({ w: i.naturalWidth, h: i.naturalHeight });
        }}
        className="rounded-lg border"
        style={{
          width: imgW || undefined,
          height: imgH || undefined,
          transform: `rotate(${rotation}deg)`,
          transformOrigin: "center center",
          transition: "transform 0.2s ease",
          position: nat ? "absolute" : "static",
          top: "50%",
          left: "50%",
          marginLeft: nat ? -imgW / 2 : undefined,
          marginTop: nat ? -imgH / 2 : undefined,
          maxWidth: "none",
        }}
      />
    </div>
  );
};


const FileViewer = ({ file, open, onClose }: FileViewerProps) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [pdfPages, setPdfPages] = useState<HTMLCanvasElement[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rotation, setRotation] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) setRotation(0);
  }, [open, file]);

  useEffect(() => {
    if (!file || !open) {
      setImageUrl(null);
      setPdfPages([]);
      setError(null);
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;

    const isImage =
      file.type === "image/png" ||
      file.type === "image/jpeg" ||
      /\.(png|jpe?g)$/i.test(file.name);

    if (isImage) {
      setPdfPages([]);
      setLoading(false);
      objectUrl = URL.createObjectURL(file);
      setImageUrl(objectUrl);
    } else {
      setImageUrl(null);
      const cached = pageCache.get(file);
      if (cached && cached.length > 0) {
        // Ya renderizado antes: pintado inmediato al navegar entre documentos.
        setPdfPages(cached);
        setLoading(false);
        setError(null);
      } else {
        setPdfPages([]);
        setLoading(true);
        setError(null);
        (async () => {
          try {
            const arrayBuffer = await file.arrayBuffer();
            if (cancelled) return;

            const pdf = await (await getPdfDocument({ data: arrayBuffer })).promise;
            const pages: HTMLCanvasElement[] = [];

            // Escala adaptada a la pantalla (nitidez sin rasterizar de más).
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            const scale = 1.5 * dpr;

            for (let i = 1; i <= pdf.numPages; i++) {
              if (cancelled) return;
              const page = await pdf.getPage(i);
              const viewport = page.getViewport({ scale });
              const canvas = document.createElement("canvas");
              canvas.width = viewport.width;
              canvas.height = viewport.height;
              const ctx = canvas.getContext("2d", { alpha: false })!;
              await page.render({ canvas, canvasContext: ctx, viewport }).promise;
              page.cleanup();
              pages.push(canvas);
              // Pintamos cada página en cuanto está lista (la 1ª aparece ya).
              if (!cancelled) {
                setPdfPages([...pages]);
                setLoading(false);
                // Cacheamos ya lo disponible por si se navega antes de acabar.
                pageCache.set(file, [...pages]);
              }
            }

            if (!cancelled) pageCache.set(file, pages);
          } catch (e) {
            console.error("Error rendering PDF:", e);
            if (!cancelled) {
              setError("No se pudo renderizar el PDF. Intenta de nuevo.");
            }
          } finally {
            if (!cancelled) setLoading(false);
          }
        })();
      }
    }



    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file, open]);

  const rotateBy = (delta: number) =>
    setRotation((r) => (((r + delta) % 360) + 360) % 360);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between gap-2">
            <DialogTitle className="truncate text-sm flex-1">
              {file?.name || "Visor de archivo"}
            </DialogTitle>
            {pdfPages.length > 0 && (
              <span className="shrink-0 rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                {pdfPages.length} {pdfPages.length === 1 ? "página" : "páginas"}
              </span>
            )}
            <div className="flex items-center gap-1 shrink-0">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => rotateBy(-90)}
                title="Girar 90° izquierda"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => rotateBy(90)}
                title="Girar 90° derecha"
              >
                <RotateCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <DialogDescription className="sr-only">
            Vista previa del documento seleccionado.
          </DialogDescription>
        </DialogHeader>
        <div
          ref={containerRef}
          className="flex-1 overflow-auto space-y-4 py-2 flex flex-col items-center"
        >
          {loading && (
            <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Cargando documento…
            </div>
          )}
          {error && (
            <div className="flex items-center justify-center py-12 text-sm text-destructive">
              {error}
            </div>
          )}
          {imageUrl && (
            <RotatableImage
              src={imageUrl}
              alt={file?.name || "documento"}
              rotation={rotation}
            />
          )}
          {pdfPages.map((cv, i) => (
            <RotatableCanvas
              key={i}
              canvas={cv}
              label={`Página ${i + 1}`}
              rotation={rotation}
            />
          ))}

        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FileViewer;
